/**
 * "/app/permission_denied" 的 `reason` query param 白名單。
 * 這是「為什麼被導到無權限頁」的唯一契約，由 new-canvas-admin（頁面所在的 repo）定義，
 * form / fever-admin / new-canvas / fever-tool 導轉過來時各自帶上同名值。
 * 設計原則：
 * 1. reason 是前端自訂的抽象值，不可放後端 error message / ResponseCode 原文
 * （避免洩漏內部服務結構，也避免後端改字串就破版）。
 * 2. 走白名單比對：不在這份清單內的值一律當成「未知」，顯示預設文案。
 * 絕不把 URL 上的字串回填畫面（反射式注入）。
 * 3. 白名單內 = 已知情境 → 不送 Sentry；未知或未帶 → 送一筆 Sentry 追蹤未預期導轉。
 * （「已知錯誤不送、未預期才送」原則）
 */
export const permissionDeniedReasons = {
    // 這個帳號在目標 sponsor 查無角色 —— 通常是連結裡的 sponsorId 不屬於自己，
    // 或 cookie 記著的 sponsorId 已被移除。後端 403 RLA-AU0-F0。
    SPONSOR_NOT_FOUND: 'sponsor_not_found',
    // 網址上的 sponsorId 在後端查無這筆資料（亂打／已刪除）。後端 400 SP-R1-FNF1 + message "404"。
    // 與 SPONSOR_NOT_FOUND 的差別：那個是「sponsor 存在但你沒有角色」，這個是「sponsor 不存在」。
    SPONSOR_NOT_EXIST: 'sponsor_not_exist',
    // 有角色，但該角色沒有這個操作／頁面的權限。後端 403 ROLE-V0-F0 / PMIVLD-AU0-F0。
    ROLE_NO_PERMISSION: 'role_no_permission',
    // 網址上根本沒有 sponsorId，前端無法決定要載入哪個主辦單位（fever-tool 門市小幫手）。
    MISSING_SPONSOR_ID: 'missing_sponsor_id',
    // 前端自算的路由權限：這個角色的 permissions 不包含當前 app 的 route。
    ROUTE_NOT_ALLOWED: 'route_not_allowed',
    // 點數中心找不到可用的 point pool。
    NO_POINT_POOL: 'no_point_pool',
    // form：表單未發布或 token 未授權（2-0101-0301-11 / 3-1000-1100-31）。
    FORM_UNPUBLISHED: 'form_unpublished',
    // form：測試時效已過期（3-3000-1101-31）。語意上非權限問題，文案需另外講清楚。
    FORM_TEST_EXPIRED: 'form_test_expired',
    // form：unique name 不正確（3-3000-1103-31）。語意上非權限問題。
    FORM_INVALID_NAME: 'form_invalid_name'
};

const knownReasons = new Set(Object.values(permissionDeniedReasons));

export function isKnownPermissionDeniedReason(reason) {
    return Boolean(reason) && knownReasons.has(reason);
}
